import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { RiskBadge, RiskBlocks, LEVEL_TEXT_COLOR } from "./RiskBadge";
import { useAuth } from "../context/AuthContext";

const CREDIT_TEXT = { high: "High", medium: "Mod", low: "Low" };
const MUTED = { color: "#a3a29c" };
const LOCKED_TEASE = 4; // blurred teaser rows shown before the sign-up CTA

function colorFor(level) {
  return { color: LEVEL_TEXT_COLOR[level] || "#73726c" };
}

const TwoLine = ({ top, bottom, color }) => (
  <td className="py-3 px-4 text-right align-middle">
    <div className="flex flex-col items-end gap-0.5 leading-tight">
      <span className="font-mono font-semibold" style={color}>{top}</span>
      {bottom && <span className="max-w-[150px] truncate text-xs text-neutral-500" title={bottom}>{bottom}</span>}
    </div>
  </td>
);

// Placeholder cell rendered blurred for locked rows — plausible-looking, but fake.
const LockedCell = ({ pct, label }) => (
  <td className="py-3 px-4 text-right align-middle">
    <div className="flex flex-col items-end gap-0.5 leading-tight blur-[5px] select-none" aria-hidden="true">
      <span className="font-mono font-semibold text-neutral-600">{pct}</span>
      <span className="text-xs text-neutral-500">{label}</span>
    </div>
  </td>
);

function LockedRow({ r }) {
  return (
    <tr data-testid="locked-row" className="border-b border-[#e8e6df] last:border-0 bg-[#fbfaf6]">
      <td className="py-3.5 px-4">
        <div className="flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
          <div className="blur-[5px] select-none" aria-hidden="true">
            <div className="font-semibold text-neutral-600">Confidential Company Inc</div>
            <div className="text-xs text-neutral-400 font-mono">XXXX · {r.sector}</div>
          </div>
        </div>
      </td>
      <LockedCell pct="24%" label="Largest customer" />
      <LockedCell pct="38%" label="United States" />
      <LockedCell pct="Mod" label="Single source" />
      <LockedCell pct="31%" label="Flagship product" />
      <LockedCell pct="18%" label="Counterparty" />
      <td className="py-3.5 px-4">
        <div className="flex justify-end blur-[5px] select-none" aria-hidden="true">
          <RiskBadge level="medium" />
        </div>
      </td>
    </tr>
  );
}

function SignupCtaRow({ hiddenCount }) {
  const { login } = useAuth();
  return (
    <tr data-testid="signup-cta-row">
      <td colSpan={7} className="p-0">
        <div className="flex flex-col items-center gap-3 border-t border-[#e8e6df] bg-[#f5f4ef] px-6 py-8 text-center">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1a1a18]">
            <Lock className="h-4 w-4 text-[#E24B4A]" />
            {hiddenCount} more {hiddenCount === 1 ? "company" : "companies"}
          </div>
          <p className="max-w-md text-sm text-neutral-600">
            Create a free account to see past the first 25.
          </p>
          <button
            data-testid="signup-cta-btn"
            onClick={() => login("leaderboard_locked_row")}
            className="rounded-full bg-[#E24B4A] px-5 py-2.5 text-sm font-medium text-white transition-transform active:scale-95 hover:opacity-90"
          >
            Sign up free with Google
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CompanyTable({ rows }) {
  const navigate = useNavigate();
  const Th = ({ children, align = "right" }) => (
    <th className={`py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-neutral-500 text-${align}`}>{children}</th>
  );

  // Keep the ranked order; show at most LOCKED_TEASE blurred locked rows, then a single CTA.
  const shown = [];
  let lockedTotal = 0;
  for (const r of rows) {
    if (r.locked) {
      lockedTotal += 1;
      if (lockedTotal > LOCKED_TEASE) continue;
    }
    shown.push(r);
  }
  const hiddenCount = Math.max(0, lockedTotal - LOCKED_TEASE);

  return (
    <div className="overflow-x-auto border border-[#e8e6df] rounded-lg bg-white">
      <table className="w-full border-collapse text-sm min-w-[920px]">
        <thead>
          <tr className="border-b border-[#e8e6df] text-left">
            <Th align="left">Company</Th>
            <Th>Customer</Th>
            <Th>Geographic</Th>
            <Th>Supplier</Th>
            <Th>Product</Th>
            <Th>Credit</Th>
            <Th>Composite</Th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            r.locked ? <LockedRow key={r.ticker || `locked-${i}`} r={r} /> : (
            <tr
              key={r.ticker}
              data-testid={`company-row-${r.ticker}`}
              onClick={() => navigate(`/company/${r.ticker}`)}
              className="border-b border-[#e8e6df] last:border-0 cursor-pointer table-row-hover hover:bg-[#f5f4ef]"
            >
              <td className="py-3.5 px-4">
                <div className="font-semibold text-[#1a1a18]">{r.name}</div>
                <div className="text-xs text-neutral-400 font-mono">{r.ticker} · {r.sector}</div>
              </td>
              <TwoLine
                top={r.customer.absent ? "<10%" : `${r.customer.top_pct}%`}
                bottom={r.customer.absent ? "No single customer" : r.customer.top_entity}
                color={r.customer.absent ? MUTED : colorFor(r.customer.level)}
              />
              <TwoLine
                top={r.geographic.absent ? "—" : `${r.geographic.top_pct}%`}
                bottom={r.geographic.absent ? "Not disclosed" : r.geographic.top_entity}
                color={r.geographic.absent ? MUTED : colorFor(r.geographic.level)}
              />
              <TwoLine
                top={r.supplier.absent ? "—"
                  : (r.supplier.quantified && r.supplier.top_pct != null
                      ? `${r.supplier.top_pct}%`
                      : (CREDIT_TEXT[r.supplier.level] || "—"))}
                bottom={r.supplier.absent ? "Diversified" : r.supplier.top_entity}
                color={r.supplier.absent ? MUTED : colorFor(r.supplier.level)}
              />
              <TwoLine
                top={r.product.absent ? "—" : `${r.product.top_pct}%`}
                bottom={r.product.absent ? "No single product" : r.product.top_entity}
                color={r.product.absent ? MUTED : colorFor(r.product.level)}
              />
              <TwoLine
                top={r.credit.absent ? "—" : `${r.credit.top_pct}%`}
                bottom={r.credit.absent ? "No single counterparty" : r.credit.top_entity}
                color={r.credit.absent ? MUTED : colorFor(r.credit.level)}
              />
              <td className="py-3.5 px-4">
                <div className="flex items-center justify-end gap-3">
                  <RiskBlocks scores={r.scores} />
                  <RiskBadge level={r.composite_level} testid={`composite-badge-${r.ticker}`} />
                </div>
              </td>
            </tr>
            )
          ))}
          {lockedTotal > 0 && <SignupCtaRow hiddenCount={hiddenCount || lockedTotal} />}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="py-10 text-center text-neutral-400">No companies match these filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
